import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, createElementVNode as _createElementVNode, Fragment as _Fragment, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-3c48344c"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "tab-width" }

import { ref, computed, onBeforeMount } from 'vue';
// import { useQuasar } from 'quasar';

// Stores
import { useUiStore } from '@stores/ui';

// Personal components
import QPageTitle from '@components/QPageTitle.vue';
import QAdminVideoList from '@pages/admin/components/QAdminVideoList.vue';
import QOfflineBanner from 'src/view/components/QOfflineBanner.vue';

// Personal libs
import { IProgram, IVideo } from '@shared/interface/model.interface';
import { RoleEnum } from '@shared/enum/role.enum';
import { UpdateManagerProgram } from '@shared/service/updateManager/update-manager-program.service';
import { RepositoryStrategyEnum } from '@shared/enum/repository-strategy.enum';
import { VideoStatusEnum } from '@shared/enum/video-status.enum';
import { sessionManager } from '@shared/service/sessionManager/session-manager.class';

enum VideoTabEnum {
  MY_VIDEOS = 'myVideos',
  TO_VALIDATE = 'videosToValidate',
}

/**
 * Stores
 */

export default /*#__PURE__*/_defineComponent({
  __name: 'AdminVideoPage',
  setup(__props) {

// Vendor
const uiStore = useUiStore();

/**
 * Reactive data
 */
const tab = ref();
const loading = ref<boolean>(false);

/**
 * Computed
 */
const programs = computed<IProgram[]>(() => uiStore.programs ?? []);
const connectedUser = computed(() => sessionManager.currentUser.value);
const isAdmin = computed<boolean>(
  () => connectedUser.value?.roles?.includes(RoleEnum.ROLE_ADMIN) === true,
);
const isSuperAdmin = computed<boolean>(
  () =>
    connectedUser.value?.roles?.includes(RoleEnum.ROLE_SUPER_ADMIN) === true,
);
const canValidateVideo = computed<boolean>(
  () =>
    connectedUser.value?.roles?.includes(RoleEnum.ROLE_VIDEO_VALIDATE) ===
      true || isSuperAdmin.value,
);
const canAddVideo = computed<boolean>(
  () =>
    connectedUser.value?.roles?.includes(RoleEnum.ROLE_VIDEO_ADD) === true ||
    isSuperAdmin.value,
);
const programWithMyValidatedVideos = computed<IProgram[]>(() => {
  const temp: IProgram[] = [];

  programs.value.forEach((p) => {
    const videos: IVideo[] =
      p.videos?.filter(
        (v) =>
          v.status === VideoStatusEnum.VALIDATED &&
          v.creator?.email === connectedUser.value?.email,
      ) || [];

    if (videos.length > 0) {
      temp.push({
        ...p,
        videos: videos,
      });
    }
  });

  return temp;
});
const programWithMyVideosToValidate = computed<IProgram[]>(() => {
  const temp: IProgram[] = [];

  programs.value.forEach((p) => {
    const videos: IVideo[] =
      p.videos?.filter(
        (v) =>
          v.status === VideoStatusEnum.TO_VALIDATE &&
          v.creator?.email === connectedUser.value?.email,
      ) || [];

    if (videos.length > 0) {
      temp.push({
        ...p,
        videos: videos,
      });
    }
  });

  return temp;
});
const programWithMyDraftVideos = computed<IProgram[]>(() => {
  const temp: IProgram[] = [];

  programs.value.forEach((p) => {
    const videos: IVideo[] =
      p.videos?.filter(
        (v) =>
          v.status === VideoStatusEnum.DRAFT &&
          v.creator?.email === connectedUser.value?.email,
      ) || [];

    if (videos.length > 0) {
      temp.push({
        ...p,
        videos: videos,
      });
    }
  });

  return temp;
});

const videosToValidate = computed<IProgram[]>(() => {
  const temp: IProgram[] = [];

  programs.value.forEach((p) => {
    const videos: IVideo[] =
      p.videos?.filter((v) => v.status === VideoStatusEnum.TO_VALIDATE) || [];

    if (videos.length > 0) {
      temp.push({
        ...p,
        videos: videos,
      });
    }
  });

  return temp;
});
const videosValidated = computed<IProgram[]>(() => {
  const temp: IProgram[] = [];

  programs.value.forEach((p) => {
    const videos: IVideo[] =
      p.videos?.filter((v) => v.status === VideoStatusEnum.VALIDATED) || [];

    if (videos.length > 0) {
      temp.push({
        ...p,
        videos: videos,
      });
    }
  });

  return temp;
});

const nbMyVideo = computed<number>(() => {
  return (
    programWithMyValidatedVideos.value.reduce(
      (prev, current) => prev + (current?.videos?.length ?? 0),
      0,
    ) +
    programWithMyDraftVideos.value.reduce(
      (prev, current) => prev + (current?.videos?.length ?? 0),
      0,
    ) +
    programWithMyVideosToValidate.value.reduce(
      (prev, current) => prev + (current?.videos?.length ?? 0),
      0,
    )
  );
});

const nbVideoToValidate = computed<number>(() =>
  videosToValidate.value.reduce(
    (prev, current) => prev + (current?.videos?.length ?? 0),
    0,
  ),
);

/**
 * Init datas
 */
new UpdateManagerProgram.ProgramAll().exec(
  RepositoryStrategyEnum.NETWORK_FIRST,
);

/**
 * Hooks
 */
onBeforeMount(() => {
  if (canAddVideo.value) {
    tab.value = VideoTabEnum.MY_VIDEOS;
  } else if (canValidateVideo.value) {
    tab.value = VideoTabEnum.TO_VALIDATE;
  } else {
    tab.value = undefined;
  }
});

return (_ctx: any,_cache: any) => {
  const _component_q_badge = _resolveComponent("q-badge")!
  const _component_q_tab = _resolveComponent("q-tab")!
  const _component_q_tabs = _resolveComponent("q-tabs")!
  const _component_q_separator = _resolveComponent("q-separator")!
  const _component_q_tab_panel = _resolveComponent("q-tab-panel")!
  const _component_q_tab_panels = _resolveComponent("q-tab-panels")!
  const _component_q_card = _resolveComponent("q-card")!
  const _component_q_spinner = _resolveComponent("q-spinner")!
  const _component_q_inner_loading = _resolveComponent("q-inner-loading")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(QPageTitle, { title: 'Admin: vidéos' }),
    _createElementVNode("div", _hoisted_1, [
      _createVNode(_component_q_card, null, {
        default: _withCtx(() => [
          _createVNode(_component_q_tabs, {
            modelValue: tab.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((tab).value = $event)),
            align: "justify",
            class: "shadow-2"
          }, {
            default: _withCtx(() => [
              (canAddVideo.value)
                ? (_openBlock(), _createBlock(_component_q_tab, {
                    key: 0,
                    name: VideoTabEnum.MY_VIDEOS,
                    icon: "videocam",
                    label: "Mes vidéos"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_q_badge, {
                        color: "primary",
                        floating: "",
                        title: `Mes vidéos : ${nbMyVideo.value > 9 ? '9+' : nbMyVideo.value} vidéo${
              nbMyVideo.value > 1 ? 's' : ''
            }`
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(nbMyVideo.value > 9 ? '9+' : nbMyVideo.value), 1 /* TEXT */)
                        ]),
                        _: 1 /* STABLE */
                      }, 8 /* PROPS */, ["title"])
                    ]),
                    _: 1 /* STABLE */
                  }, 8 /* PROPS */, ["name"]))
                : _createCommentVNode("v-if", true),
              (canValidateVideo.value)
                ? (_openBlock(), _createBlock(_component_q_tab, {
                    key: 1,
                    name: VideoTabEnum.TO_VALIDATE,
                    icon: "check",
                    label: "A valider"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_q_badge, {
                        color: "orange",
                        "text-color": "black",
                        floating: "",
                        title: `${nbVideoToValidate.value > 9 ? '9+' : nbVideoToValidate.value} vidéo${
              nbVideoToValidate.value > 1 ? 's' : ''
            } à valider`
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(nbVideoToValidate.value > 9 ? '9+' : nbVideoToValidate.value), 1 /* TEXT */)
                        ]),
                        _: 1 /* STABLE */
                      }, 8 /* PROPS */, ["title"])
                    ]),
                    _: 1 /* STABLE */
                  }, 8 /* PROPS */, ["name"]))
                : _createCommentVNode("v-if", true)
            ]),
            _: 1 /* STABLE */
          }, 8 /* PROPS */, ["modelValue"]),
          _createVNode(_component_q_separator),
          _createVNode(_component_q_tab_panels, {
            modelValue: tab.value,
            "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((tab).value = $event)),
            animated: ""
          }, {
            default: _withCtx(() => [
              _createCommentVNode(" My videos "),
              (canAddVideo.value)
                ? (_openBlock(), _createBlock(_component_q_tab_panel, {
                    key: 0,
                    name: VideoTabEnum.MY_VIDEOS
                  }, {
                    default: _withCtx(() => [
                      _createCommentVNode(" Validated videos "),
                      _createVNode(QAdminVideoList, {
                        title: 'Mes vidéos validées',
                        status: _unref(VideoStatusEnum).VALIDATED,
                        "can-validate-video": canValidateVideo.value || isSuperAdmin.value,
                        programs: programWithMyValidatedVideos.value,
                        "can-delete-video": true,
                        onLoading: _cache[1] || (_cache[1] = ($event: any) => (loading.value = true)),
                        onLoaded: _cache[2] || (_cache[2] = ($event: any) => (loading.value = false))
                      }, null, 8 /* PROPS */, ["status", "can-validate-video", "programs"]),
                      _createCommentVNode(" Videos to validate "),
                      _createVNode(QAdminVideoList, {
                        title: 'Mes vidéos en attente de validation',
                        status: _unref(VideoStatusEnum).TO_VALIDATE,
                        "can-validate-video": canValidateVideo.value || isSuperAdmin.value,
                        programs: programWithMyVideosToValidate.value,
                        "can-delete-video": true,
                        onLoading: _cache[3] || (_cache[3] = ($event: any) => (loading.value = true)),
                        onLoaded: _cache[4] || (_cache[4] = ($event: any) => (loading.value = false))
                      }, null, 8 /* PROPS */, ["status", "can-validate-video", "programs"]),
                      _createCommentVNode(" Draft videos "),
                      _createVNode(QAdminVideoList, {
                        title: 'Mes vidéos brouillon',
                        status: _unref(VideoStatusEnum).DRAFT,
                        "can-validate-video": canValidateVideo.value || isSuperAdmin.value,
                        programs: programWithMyDraftVideos.value,
                        "can-delete-video": canValidateVideo.value && isSuperAdmin.value,
                        onLoading: _cache[5] || (_cache[5] = ($event: any) => (loading.value = true)),
                        onLoaded: _cache[6] || (_cache[6] = ($event: any) => (loading.value = false))
                      }, null, 8 /* PROPS */, ["status", "can-validate-video", "programs", "can-delete-video"])
                    ]),
                    _: 1 /* STABLE */
                  }, 8 /* PROPS */, ["name"]))
                : _createCommentVNode("v-if", true),
              _createCommentVNode(" All videos "),
              (canValidateVideo.value)
                ? (_openBlock(), _createBlock(_component_q_tab_panel, {
                    key: 1,
                    name: VideoTabEnum.TO_VALIDATE
                  }, {
                    default: _withCtx(() => [
                      _createVNode(QAdminVideoList, {
                        title: 'Vidéos à valider',
                        status: _unref(VideoStatusEnum).TO_VALIDATE,
                        "can-validate-video": canValidateVideo.value || isSuperAdmin.value,
                        "can-delete-video": isSuperAdmin.value || isAdmin.value,
                        programs: videosToValidate.value,
                        onLoading: _cache[7] || (_cache[7] = ($event: any) => (loading.value = true)),
                        onLoaded: _cache[8] || (_cache[8] = ($event: any) => (loading.value = false))
                      }, null, 8 /* PROPS */, ["status", "can-validate-video", "can-delete-video", "programs"]),
                      _createVNode(QAdminVideoList, {
                        title: 'Vidéos validées',
                        status: _unref(VideoStatusEnum).VALIDATED,
                        "can-validate-video": canValidateVideo.value || isSuperAdmin.value,
                        "can-delete-video": isSuperAdmin.value || isAdmin.value,
                        programs: videosValidated.value,
                        onLoading: _cache[9] || (_cache[9] = ($event: any) => (loading.value = true)),
                        onLoaded: _cache[10] || (_cache[10] = ($event: any) => (loading.value = false))
                      }, null, 8 /* PROPS */, ["status", "can-validate-video", "can-delete-video", "programs"])
                    ]),
                    _: 1 /* STABLE */
                  }, 8 /* PROPS */, ["name"]))
                : _createCommentVNode("v-if", true)
            ]),
            _: 1 /* STABLE */
          }, 8 /* PROPS */, ["modelValue"])
        ]),
        _: 1 /* STABLE */
      }),
      _createVNode(_component_q_inner_loading, { showing: loading.value }, {
        default: _withCtx(() => [
          _createVNode(_component_q_spinner, {
            size: "100px",
            color: "primary"
          })
        ]),
        _: 1 /* STABLE */
      }, 8 /* PROPS */, ["showing"])
    ]),
    _createVNode(QOfflineBanner)
  ], 64 /* STABLE_FRAGMENT */))
}
}

})